import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Download, Lock, ShieldCheck } from "lucide-react";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { COMPLIANCE_MODE } from "@/config/compliance";
import {
  requestLeadMagnet,
  type FeaturedLeadMagnet,
} from "@/lib/leadMagnet.functions";

export function LeadMagnetForm({
  resource,
  variant = "full",
}: {
  resource: FeaturedLeadMagnet;
  variant?: "full" | "compact";
}) {
  const submit = useServerFn(requestLeadMagnet);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [website, setWebsite] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ url: string; emailed: boolean } | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const res = await submit({
        data: {
          resourceId: resource.id,
          email: email.trim().toLowerCase(),
          firstName: firstName.trim(),
          consentMarketing: consent,
          website,
        },
      });
      setDone({ url: res.url, emailed: res.emailed });
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  const inputCls =
    "w-full border border-bronze-600/30 bg-white px-3 py-2.5 font-sans text-[14px] text-charcoal-900 outline-none placeholder:text-charcoal-500/70 focus:border-bronze-600";

  return (
    <div
      className={`grid gap-8 ${variant === "full" ? "md:grid-cols-[1.1fr_1fr]" : ""} border border-bronze-600/20 bg-white p-6 shadow-[0_18px_40px_-32px_rgba(168,139,92,0.6)] md:p-8`}
      style={{ borderRadius: 6 }}
    >
      <div>
        <Eyebrow>Free Download</Eyebrow>
        <h2 className="mt-4 font-display text-[26px] leading-tight text-navy-900 md:text-[32px]">
          {resource.headline}
        </h2>
        <p className="mt-3 font-sans text-[15px] leading-relaxed text-charcoal-500">
          {resource.subhead}
        </p>
        {resource.cover_image_url && (
          <img
            src={resource.cover_image_url}
            alt={`Cover of ${resource.title}`}
            loading="lazy"
            className="mt-6 w-full max-w-[280px] border border-bronze-600/20 object-cover"
          />
        )}
        <p className="mt-5 flex items-center gap-2 font-sans text-[12px] text-charcoal-500">
          <ShieldCheck className="h-4 w-4 text-bronze-600" strokeWidth={1.5} />
          {resource.download_count.toLocaleString()} families have downloaded this guide.
        </p>
      </div>

      <div className="border-t border-bronze-600/20 pt-6 md:border-l md:border-t-0 md:pl-8 md:pt-0">
        {done ? (
          <div>
            <h3 className="font-display text-[22px] text-navy-900">Your guide is ready.</h3>
            <p className="mt-2 font-sans text-[14px] leading-relaxed text-charcoal-500">
              {done.emailed
                ? "We also emailed you a copy. The link stays active for 60 minutes."
                : "Your download link stays active for 60 minutes."}
            </p>
            <a
              href={done.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 bg-bronze-600 px-5 py-2.5 font-sans text-[12px] font-semibold uppercase tracking-[0.14em] text-navy-800"
              style={{ borderRadius: 2 }}
            >
              <Download className="h-4 w-4" /> Download again
            </a>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="lm-first"
                className="font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-charcoal-500"
              >
                First name (optional)
              </label>
              <input
                id="lm-first"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
                className={`mt-1 ${inputCls}`}
                placeholder="Jordan"
              />
            </div>
            <div>
              <label
                htmlFor="lm-email"
                className="font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-charcoal-500"
              >
                Email address
              </label>
              <input
                id="lm-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                className={`mt-1 ${inputCls}`}
                placeholder="you@example.com"
              />
            </div>

            {/* honeypot */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="hidden"
            />

            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 h-4 w-4 accent-[#A88B5C]"
              />
              <span className="font-sans text-[12px] leading-relaxed text-charcoal-500">
                {COMPLIANCE_MODE
                  ? "Send me occasional educational resources from Currency Thoughts. I can unsubscribe at any time."
                  : "I'd like a strategist to follow up about my family's plan, and to receive educational resources."}
              </span>
            </label>

            <PrimaryButton type="submit" disabled={busy} className="w-full">
              <Download className="h-4 w-4" />
              {busy ? "Preparing…" : "Get the free guide"}
            </PrimaryButton>

            <p className="flex items-start gap-2 font-sans text-[11px] leading-relaxed text-charcoal-500">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-bronze-600" strokeWidth={1.6} />
              {COMPLIANCE_MODE
                ? "Educational material only. We never sell your information, and no one will call you unless you ask us to."
                : "We never sell your information. You control how we contact you."}
            </p>
          </form>
        )}

        <p className="mt-6 border-t border-bronze-600/20 pt-4 font-sans text-[11px] leading-relaxed text-charcoal-500">
          Currency Thoughts LLC is an independent insurance agency. Products and
          availability vary by state. Downloadable materials are educational only
          and do not constitute legal, tax, or investment advice.
        </p>
      </div>
    </div>
  );
}